import type { ClientStore } from '../state/clientStore.js';
import type { ServerClock } from '../state/clock.js';
import type { ServerMessage } from '@vitals/shared';

/**
 * Milliseconds represented by the response's `Age` header (seconds since a
 * Cloudflare edge cache HIT was generated/refreshed at origin), or 0 if the
 * header is absent, unparsable, or negative (a MISS, or a malformed value).
 */
function ageMsFrom(res: Response): number {
  const raw = res.headers.get('age');
  if (raw === null) return 0;
  const seconds = parseInt(raw, 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
}

/**
 * Fetches the edge-cached /snapshot endpoint and applies the result to the
 * store and clock. Runs in parallel with WS connect so the map can paint
 * immediately on cold load even when the Fly origin's event loop is throttled.
 *
 * The edge caches /snapshot with s-maxage=30 and stale-while-revalidate=120,
 * so a HIT can be up to ~150s stale (a background revalidation can still
 * serve the old cached body while it refreshes). Meanwhile the WS connection
 * — run in parallel by the caller — delivers its own full snapshot plus live
 * deltas, and is always at least as fresh. Bootstrap's only job is painting
 * the map fast when it happens to WIN that race (a layer the WS hasn't
 * populated yet); it must never clobber a layer the WS already delivered
 * with an edge-cached response that can be minutes old. So each layer is
 * applied only if the store doesn't already have it, and the clock is only
 * (re)synced from this stale response when doing so can't regress a fresher
 * WS sync (see the shouldSyncClock comment below).
 *
 * Never throws — if the fetch fails for any reason the WS path is the safety net.
 */
export async function bootstrapSnapshot(opts: {
  baseUrl: string;
  store: ClientStore;
  clock: ServerClock;
}): Promise<void> {
  try {
    const res = await fetch(`${opts.baseUrl}/snapshot`);
    if (!res.ok) return;
    const ageMs = ageMsFrom(res);
    const msg = JSON.parse(await res.text()) as ServerMessage;
    if (msg.type === 'snapshot') {
      // Only fill layers the store doesn't already have — a layer already
      // present means the WS path (always fresher, see the function comment)
      // won the race for it, and this edge-cached response must not stomp it.
      const newStates = msg.states.filter((s) => opts.store.get(s.layerId) === undefined);

      // The clock sync carries the same staleness risk: syncing from this
      // response's (possibly ~150s-old) serverTime could regress a clock
      // already synced fresher by the WS. Sync is safe exactly when either
      // (a) at least one layer above was actually new — this bootstrap did
      // useful work and its serverTime is the best data-freshness signal we
      // have for those layers — or (b) the clock has never synced at all, in
      // which case a stale estimate still beats no estimate. Otherwise (the
      // store already had every layer this snapshot carries, i.e. the WS
      // fully won) skip the sync so a late, stale bootstrap can't drag the
      // clock backward.
      const shouldSyncClock = newStates.length > 0 || opts.clock.msSinceLastSync() === null;
      if (shouldSyncClock) opts.clock.sync(msg.serverTime + ageMs, ageMs);

      newStates.forEach((s) => opts.store.apply(s));
    }
  } catch (err) {
    console.warn('[bootstrap] snapshot fetch failed', err);
  }
}
